/**
 * Smartlink inbound and outbound orders.
 *
 * Two kinds land here and the difference decides whether a document can ever be posted:
 *
 *   pushed  — we created it from a PO or SO (`LSKIN-…` / `LSKOUT-…`), so the source document is
 *             recoverable from the order number and a completed scan produces a GR or DO
 *   PDA     — created on the handheld with no SQL Accounting document behind it, so it can be
 *             scanned and submitted but will never post anything
 *
 * Showing that plainly saves the "why did this scan not produce a receipt?" conversation.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, qty } from '../lib/api';
import { MYT } from '../lib/time';
import { Card, Table, Loading, Empty, cell, when } from '../components/ui';

type Order = {
  order_no: string;
  direction: 'IN' | 'OUT';
  order_type: string | null;
  status: number | null;
  link_order_no: string | null;
  source_code: string | null;
  target_code: string | null;
  order_date: string | null;
  submit_time: string | null;
  operator: string | null;
  plan_qty: string;
  real_qty: string;
  skus: number;
  epcs: number;
  posted_docno: string | null;
  origin: 'pushed' | 'pda-supplier' | 'pda';
};

type Line = {
  sku: string;
  parent_sku: string;
  piece: string | null;
  description: string | null;
  plan_qty: string;
  real_qty: string;
  epcs: number;
};

/** Smartlink header status. 3 is submit-FAILED and is not in their reference docs. */
const STATUS: Record<number, { label: string; cls: string }> = {
  1: { label: 'pending', cls: 'bg-slate-100 text-slate-600' },
  2: { label: 'in progress', cls: 'bg-sky-light/30 text-brand-800' },
  3: { label: 'submit failed', cls: 'bg-alert/20 text-alert' },
  10: { label: 'complete', cls: 'bg-emerald-50 text-emerald-700' },
};

export default function RfidOrders() {
  const [dir, setDir] = useState<'ALL' | 'IN' | 'OUT'>('ALL');
  const [open, setOpen] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ['rfid-orders', dir],
    queryFn: () =>
      api.get<{ orders: Order[] }>(
        `/api/v1/rfid/orders${dir === 'ALL' ? '' : `?direction=${dir}`}`,
      ),
    refetchInterval: 60_000,
  });

  const detail = useQuery({
    queryKey: ['rfid-order', open],
    enabled: open !== null,
    queryFn: () =>
      api.get<{
        order: Order;
        lines: Line[];
        epcs: Array<{
          sku: string;
          epc: string;
          unique_code: string | null;
          scan_time: string | null;
        }>;
      }>(`/api/v1/rfid/orders/${encodeURIComponent(open ?? '')}`),
  });

  return (
    <div className="space-y-6">
      <Card
        title="RFID orders"
        subtitle="Inbound and outbound orders in Smartlink. Scanned quantity is what the tags actually read."
        actions={
          <div className="flex gap-1">
            {(['ALL', 'IN', 'OUT'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDir(d)}
                className={`rounded px-2.5 py-1 text-xs ${
                  dir === d
                    ? 'bg-brand-600 text-white'
                    : 'border border-slate-300 hover:bg-brand-50'
                }`}
              >
                {d === 'ALL' ? 'All' : d === 'IN' ? 'Inbound' : 'Outbound'}
              </button>
            ))}
          </div>
        }
      >
        {orders.isLoading ? (
          <Loading />
        ) : (orders.data?.orders ?? []).length === 0 ? (
          <Empty>No RFID orders yet. They appear within 15 minutes of being created.</Empty>
        ) : (
          <Table
            head={[
              'Order',
              'Dir',
              'Origin',
              'From → To',
              `When (${MYT})`,
              'Status',
              'Planned',
              'Scanned',
              'EPCs',
              'Posted',
              '',
            ]}
            rows={(orders.data?.orders ?? []).map((o) => {
              const st = STATUS[Number(o.status)] ?? {
                label: String(o.status ?? '—'),
                cls: 'bg-slate-100',
              };
              return [
                <span className="font-mono text-xs">{o.order_no}</span>,
                o.direction,
                // Most PDA-native orders have no source document and can never post. The
                // exception is a supplier inbound from LSK MattressWorld, which raises its own.
                o.origin === 'pushed' ? (
                  <span className="text-brand-700">from PO/SO</span>
                ) : o.origin === 'pda-supplier' ? (
                  <span
                    className="text-brand-500"
                    title="Supplier inbound scanned on the handheld — raises its own PO and GRN"
                  >
                    PDA · supplier
                  </span>
                ) : (
                  <span
                    className="text-slate-500"
                    title="Created on the handheld — no SQL Accounting document behind it"
                  >
                    PDA
                  </span>
                ),
                `${o.source_code ?? '—'} → ${o.target_code ?? '—'}`,
                when(o.submit_time ?? o.order_date),
                <span className={`rounded px-2 py-0.5 text-xs ${st.cls}`}>{st.label}</span>,
                cell(Number(o.plan_qty), qty(o.plan_qty)),
                cell(Number(o.real_qty), <span className="font-medium">{qty(o.real_qty)}</span>),
                cell(o.epcs, String(o.epcs)),
                o.posted_docno ? (
                  <span className="text-emerald-700">{o.posted_docno}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                ),
                <button
                  onClick={() => setOpen(open === o.order_no ? null : o.order_no)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                >
                  {open === o.order_no ? 'Hide' : 'Lines'}
                </button>,
              ];
            })}
          />
        )}
      </Card>

      {open && (
        <Card
          title={`Lines — ${open}`}
          subtitle="Smartlink SKUs, with the SQL Accounting parent they resolve to."
        >
          {detail.isLoading ? (
            <Loading />
          ) : (
            <>
              <Table
                head={[
                  'Smartlink SKU',
                  'Resolves to',
                  'Piece',
                  'Description',
                  'Planned',
                  'Scanned',
                  'EPCs',
                ]}
                rows={(detail.data?.lines ?? []).map((l) => [
                  <span className="font-mono text-xs">{l.sku}</span>,
                  l.parent_sku === l.sku ? (
                    <span className="text-slate-400">same</span>
                  ) : (
                    <span className="font-mono text-xs text-brand-700">{l.parent_sku}</span>
                  ),
                  l.piece ?? '—',
                  l.description ?? '—',
                  qty(l.plan_qty),
                  cell(Number(l.real_qty), <span className="font-medium">{qty(l.real_qty)}</span>),
                  String(l.epcs),
                ])}
              />

              {(detail.data?.epcs ?? []).length > 0 && (
                <div className="mt-5 border-t border-slate-100 pt-5">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Tags read — each EPC is one physical unit and its warranty serial
                  </h3>
                  <Table
                    head={['EPC', 'SKU', 'Unique code', `Scanned at (${MYT})`]}
                    rows={(detail.data?.epcs ?? []).map((e) => [
                      <span className="font-mono text-xs">{e.epc}</span>,
                      <span className="font-mono text-xs">{e.sku}</span>,
                      e.unique_code ?? '—',
                      when(e.scan_time),
                    ])}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
}
